import {
  Calendar,
  Eye,
  FileCheck,
  MoreHorizontal,
  Paperclip,
  Pencil,
  Send,
  Sparkles,
  Trash2,
  Users,
} from 'lucide-react';
import { EmptyState } from '@/components/ui/EmptyState';
import { Menu } from '@/components/ui/Menu';
import { QueryError } from '@/components/ui/QueryError';
import { formatDate } from '@/lib/format';
import { cn } from '@/lib/utils';
import type { ClassActivity } from '@/types/activity';
import { formatFileSize } from '../../activities/utils';
import {
  ACTIVITY_STATE_PILL,
  activityState,
  getActivityTheme,
  type TabKey,
} from './activityTheme';
import { ActivityPagination } from './ActivityPagination';

interface ActivityTableProps {
  isLoading: boolean;
  error: { message: string } | null;
  activities: ClassActivity[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  searchQuery: string;
  hasExtraFilters: boolean;
  activeTab: TabKey;
  today: string;
  onRetry: () => void;
  onReview: (activity: ClassActivity) => void;
  onEdit: (activity: ClassActivity) => void;
  /** Opens the guidelines sheet in a preview, with Download inside it. */
  onViewGuideline: (activity: ClassActivity) => void;
  /** Releases a draft to the class as it stands. */
  onPublish: (activity: ClassActivity) => void;
  onDelete: (activity: ClassActivity) => void;
}

export function ActivityTable({
  isLoading,
  error,
  activities,
  totalCount,
  page,
  pageSize,
  totalPages,
  onPageChange,
  onPageSizeChange,
  searchQuery,
  hasExtraFilters,
  activeTab,
  today,
  onRetry,
  onReview,
  onEdit,
  onViewGuideline,
  onPublish,
  onDelete,
}: ActivityTableProps) {
  return (
    <div className="card bg-surface border border-line rounded-[16px] overflow-hidden shadow-2xs">
      {/* Scrollable Table Container */}
      <div className="relative overflow-x-auto table-scrollbar">
        <table className="w-full text-left border-separate border-spacing-0 min-w-[890px]">
          <thead>
            <tr className="bg-surface text-[11px] font-bold text-ink-3 uppercase tracking-wider">
              <th className="py-3.5 px-5 sm:sticky sm:left-0 z-10 bg-surface sm:border-r sm:border-b border-line shadow-2xs w-[280px] min-w-[280px] max-w-[280px]">
                Activity
              </th>
              <th className="py-3.5 px-4 min-w-[170px] border-b border-line">Date &amp; Time</th>
              <th className="py-3.5 px-4 text-center min-w-[110px] border-b border-line">Type</th>
              <th className="py-3.5 px-4 text-center min-w-[150px] border-b border-line">Participants</th>
              <th className="py-3.5 px-4 text-center min-w-[120px] border-b border-line">Status</th>
              <th className="py-3.5 px-4 text-right sm:sticky sm:right-0 z-10 bg-surface sm:border-l sm:border-b border-line shadow-2xs w-[60px] min-w-[60px]">
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>

          {/* Table Body */}
          <tbody>
            {isLoading &&
              Array.from({ length: 5 }, (_, index) => (
                <tr key={`skel-${index}`} className="border-b border-line">
                  <td className="sm:sticky sm:left-0 z-10 bg-white dark:bg-[#111c2e] py-4 px-5 sm:border-r border-b border-line shadow-2xs w-[280px] min-w-[280px] max-w-[280px]">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-[10px] bg-slate-200 dark:bg-slate-800 animate-pulse shrink-0" />
                      <div className="flex flex-col gap-1.5 flex-1">
                        <div className="h-4 w-3/4 bg-slate-200 dark:bg-slate-800 rounded animate-pulse" />
                        <div className="h-3 w-1/2 bg-slate-200 dark:bg-slate-800 rounded animate-pulse" />
                      </div>
                    </div>
                  </td>
                  <td className="py-4 px-4 min-w-[170px] border-b border-line">
                    <div className="h-4 w-24 bg-slate-200 dark:bg-slate-800 rounded animate-pulse mb-1" />
                    <div className="h-3 w-28 bg-slate-200 dark:bg-slate-800 rounded animate-pulse" />
                  </td>
                  <td className="py-4 px-4 text-center min-w-[110px] border-b border-line">
                    <div className="h-6 w-16 mx-auto bg-slate-200 dark:bg-slate-800 rounded-pill animate-pulse" />
                  </td>
                  <td className="py-4 px-4 text-center min-w-[150px] border-b border-line">
                    <div className="h-4 w-20 mx-auto bg-slate-200 dark:bg-slate-800 rounded animate-pulse mb-1" />
                    <div className="h-3 w-24 mx-auto bg-slate-200 dark:bg-slate-800 rounded animate-pulse" />
                  </td>
                  <td className="py-4 px-4 text-center min-w-[120px] border-b border-line">
                    <div className="h-6 w-20 mx-auto bg-slate-200 dark:bg-slate-800 rounded-pill animate-pulse" />
                  </td>
                  <td className="sm:sticky sm:right-0 z-10 bg-white dark:bg-[#111c2e] py-4 px-4 text-right sm:border-l border-b border-line shadow-2xs w-[60px] min-w-[60px]">
                    <div className="h-8 w-8 ml-auto bg-slate-200 dark:bg-slate-800 rounded-full animate-pulse" />
                  </td>
                </tr>
              ))}

            {!isLoading && error && (
              <tr>
                <td colSpan={6} className="p-6 border-b border-line">
                  <QueryError
                    title="Could not load your activities"
                    error={error}
                    onRetry={onRetry}
                  />
                </td>
              </tr>
            )}

            {!isLoading && !error && activities.length === 0 && (
              <tr>
                <td colSpan={6} className="p-8 border-b border-line">
                  <EmptyState
                    icon={<Sparkles className="h-5 w-5" />}
                    title={
                      searchQuery || hasExtraFilters || activeTab !== 'all'
                        ? 'No activities match'
                        : 'No activities set yet'
                    }
                    description={
                      searchQuery || hasExtraFilters || activeTab !== 'all'
                        ? 'Nothing matches your selected tab or filters. Clear them to see all activities.'
                        : 'Assign your first project, practical or presentation — it appears here with real-time participation tracking.'
                    }
                  />
                </td>
              </tr>
            )}

            {!isLoading &&
              !error &&
              activities.length > 0 &&
              activities.map((activity) => {
                const theme = getActivityTheme(activity.activityType);
                const IconComp = theme.icon;

                const state = activityState(activity, today);
                // Withdrawn work is gone from the server's reads: its review,
                // its guidelines and withdrawing it again would all 404.
                const isWithdrawn = state === 'cancelled';
                // Nobody has been given a draft, so there is nothing to review:
                // opening it means finishing it.
                const isDraft = state === 'draft';
                const open = isDraft ? onEdit : onReview;
                const { label: statusLabel, className: statusClass } = ACTIVITY_STATE_PILL[state];

                const formattedDueDate = new Date(
                  `${activity.dueDate}T00:00:00Z`,
                ).toLocaleDateString('en-GB', {
                  day: '2-digit',
                  month: 'short',
                  year: 'numeric',
                  timeZone: 'UTC',
                });

                return (
                  <tr
                    key={activity.uuid}
                    className="group transition-colors"
                  >
                    {/* Column 1: Activity (Sticky Left) */}
                    <td className="sm:sticky sm:left-0 z-10 bg-white dark:bg-[#111c2e] group-hover:bg-slate-50 dark:group-hover:bg-[#16253d] py-4 px-5 sm:border-r border-b border-line shadow-2xs w-[280px] min-w-[280px] max-w-[280px] transition-colors">
                      <div className="flex items-start gap-3.5 min-w-0">
                        <span
                          className={cn(
                            'grid h-10 w-10 shrink-0 place-items-center rounded-[10px] shadow-2xs mt-0.5',
                            theme.bg,
                          )}
                        >
                          <IconComp className="h-5 w-5" />
                        </span>

                        <div className="min-w-0 flex-1">
                          <h3
                            onClick={isWithdrawn ? undefined : () => open(activity)}
                            className={cn(
                              'text-sm sm:text-base font-bold text-ink transition-colors line-clamp-1',
                              !isWithdrawn && 'hover:text-indigo-400 cursor-pointer',
                            )}
                          >
                            {activity.title}
                          </h3>
                          <p className="text-xs text-ink-3 font-medium mt-0.5 line-clamp-1">
                            {activity.description}
                          </p>

                          {activity.guideline && !isWithdrawn && (
                            <button
                              type="button"
                              onClick={() => onViewGuideline(activity)}
                              aria-label={`Preview ${activity.guideline.name}`}
                              className="inline-flex items-center gap-1.5 mt-1.5 rounded-field border border-line bg-surface-2 px-2 py-0.5 text-left text-2xs text-ink-2 font-medium transition-colors hover:border-indigo-500/50 hover:text-indigo-400"
                            >
                              <Paperclip className="h-3 w-3 shrink-0 text-indigo-400" />
                              <span className="truncate max-w-[130px]">
                                {activity.guideline.name}
                              </span>
                              {activity.guideline.sizeBytes !== null && (
                                <span className="text-ink-3">
                                  ({formatFileSize(activity.guideline.sizeBytes)})
                                </span>
                              )}
                            </button>
                          )}
                        </div>
                      </div>
                    </td>

                    {/* Column 2: Date & Time */}
                    <td className="bg-surface group-hover:bg-slate-50/80 dark:group-hover:bg-surface-2 py-4 px-4 min-w-[170px] whitespace-nowrap align-middle border-b border-line transition-colors">
                      <p className="text-xs font-bold text-ink flex items-center gap-1.5">
                        <Calendar className="h-3.5 w-3.5 shrink-0 text-rose-400" />
                        <span className="text-sm">
                          <span className="font-semibold text-ink-3">Due :</span> {formattedDueDate}
                        </span>
                      </p>
                      <p className="text-2xs text-ink-3 font-medium mt-0.5 flex items-center gap-1">
                        <span className="text-xs">
                          Posted {formatDate(activity.assignedDate)} ·{' '}
                          {activity.classSection.displayName}
                        </span>
                      </p>
                    </td>

                    {/* Column 3: Type */}
                    <td className="bg-surface group-hover:bg-slate-50/80 dark:group-hover:bg-surface-2 py-4 px-4 text-center min-w-[110px] whitespace-nowrap align-middle border-b border-line transition-colors">
                      <span
                        className={cn(
                          'px-2.5 py-1 rounded-pill text-sm font-bold border inline-block',
                          theme.typeTone,
                        )}
                      >
                        {theme.typeLabel}
                      </span>
                    </td>

                    {/* Column 4: Participants */}
                    <td className="bg-surface group-hover:bg-slate-50/80 dark:group-hover:bg-surface-2 py-4 px-4 text-center min-w-[150px] whitespace-nowrap align-middle border-b border-line transition-colors">
                      <p className="text-xs font-bold text-ink flex items-center justify-center gap-1.5">
                        <Users className="h-3.5 w-3.5 shrink-0 text-ink-3" />
                        <span className="text-sm">{activity.totalStudents} Students</span>
                      </p>
                      <p className="text-xs text-ink-3 font-medium mt-0.5">
                        {/* Unscored work is never graded, so "0 graded" would read
                            as marking left undone. */}
                        {activity.submissionCount} submitted
                        {activity.maxPoints !== null && ` · ${activity.gradedCount} graded`}
                      </p>
                    </td>

                    {/* Column 5: Status */}
                    <td className="bg-surface group-hover:bg-slate-50/80 dark:group-hover:bg-surface-2 py-4 px-4 text-center min-w-[120px] whitespace-nowrap align-middle border-b border-line transition-colors">
                      <span
                        className={cn(
                          'px-2.5 py-0.5 rounded-pill text-sm font-bold border inline-block text-center whitespace-nowrap',
                          statusClass,
                        )}
                      >
                        {statusLabel}
                      </span>
                    </td>

                    {/* Column 6: Actions (Sticky Right) */}
                    <td className="sm:sticky sm:right-0 z-10 bg-white dark:bg-[#111c2e] group-hover:bg-slate-50 dark:group-hover:bg-[#16253d] py-4 px-4 text-right sm:border-l border-b border-line shadow-2xs w-[60px] min-w-[60px] align-middle transition-colors">
                      {!isWithdrawn && (
                      <Menu
                        align="end"
                        label={`Actions for ${activity.title}`}
                        triggerClassName="grid h-8 w-8 place-items-center rounded-full text-ink-3 hover:text-ink hover:bg-surface-2 transition-colors cursor-pointer border-0 bg-transparent shadow-none"
                        trigger={<MoreHorizontal className="h-4 w-4" aria-hidden />}
                        items={[
                          isDraft
                            ? {
                                key: 'publish',
                                label: 'Publish',
                                icon: <Send className="h-3.5 w-3.5 text-indigo-400" />,
                                onSelect: () => onPublish(activity),
                              }
                            : {
                                key: 'review',
                                label: 'Review Submissions',
                                icon: <FileCheck className="h-3.5 w-3.5 text-indigo-400" />,
                                onSelect: () => onReview(activity),
                              },
                          {
                            key: 'edit',
                            label: 'Edit Activity',
                            icon: <Pencil className="h-3.5 w-3.5 text-emerald-400" />,
                            onSelect: () => onEdit(activity),
                          },
                          ...(activity.guideline
                            ? [
                                {
                                  key: 'guidelines',
                                  label: 'View Guidelines',
                                  icon: <Eye className="h-3.5 w-3.5 text-sky-400" />,
                                  onSelect: () => onViewGuideline(activity),
                                },
                              ]
                            : []),
                          {
                            key: 'delete',
                            label: isDraft ? 'Delete Draft' : 'Withdraw Activity',
                            icon: <Trash2 className="h-3.5 w-3.5 text-rose-500" />,
                            onSelect: () => onDelete(activity),
                          },
                        ]}
                      />
                      )}
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>

      {/* Embedded Pagination */}
      <ActivityPagination
        totalCount={totalCount}
        page={page}
        pageSize={pageSize}
        totalPages={totalPages}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
      />
    </div>
  );
}
